"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Mail, Phone, MapPin, Send, MessageSquare, Handshake, Briefcase, Mic, Lock, ArrowRight, Sparkles } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";
import { Button } from "@/components/ui/button";

const PATHS = [
  { icon: Mic, title: "Speaking & Keynote Inquiries", desc: "For conferences, corporate events, universities, and forums. Signature topics available. Past stages include the Nobel Peace Prize and the World Economic Forum.", href: "/contact/", gradient: "from-red/15", accent: "text-red" },
  { icon: Briefcase, title: "Executive Advisory & Board Service", desc: "For founders, executives, and institutions seeking the operating discipline that produces verified outcomes.", href: "/contact/", gradient: "from-purple/15", accent: "text-purple" },
  { icon: Lock, title: "Operational Engagements", desc: "Discreet, high-stakes work, by introduction only. Full operational ecosystem deployment under absolute confidentiality.", href: "/contact/", gradient: "from-emerald/15", accent: "text-emerald" },
  { icon: MessageSquare, title: "Press & Media", desc: "For journalists and media organizations seeking expert commentary on operational excellence, women in leadership, governance, and strategic intelligence.", href: "/contact/", gradient: "from-cyan/15", accent: "text-cyan" },
  { icon: Handshake, title: "Partnership & Collaboration", desc: "For strategic alliances and joint initiatives aligned with the mission of delivering intelligence-driven outcomes.", href: "/contact/", gradient: "from-amber-gold/15", accent: "text-amber-gold" },
  { icon: Mail, title: "General Contact", desc: "For all other inquiries. Every engagement begins with a single question: what needs to be won?", href: "/contact/", gradient: "from-amber-gold/15", accent: "text-amber-gold" },
];

export function ContactSection() {
  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [sent, setSent] = React.useState(false);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.includes("@") && message.length > 5) {
      setSent(true);
      setTimeout(() => setSent(false), 5000);
      setEmail("");
      setMessage("");
    }
  };

  return (
    <Section id="contact" ariaLabel="Contact and Engagement" background="default">
      <SectionHeader
        icon={Mail}
        overline="Contact"
        overlineClassName="text-amber-gold"
        pillClassName="glass-tinted-amber-gold border-amber-gold/20"
        title={
          <>
            Every Engagement Begins with a <span className="text-gradient-premium">Single Question.</span>
          </>
        }
        subtitle='"What needs to be won?" From there, the team is assembled, the operations are activated, and the strategy is executed.'
      />

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-5 lg:gap-6">
        <div className="space-y-3">
          {PATHS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <Link href={p.href} className="block group">
                <div className={`relative glass-card rounded-xl p-5 bg-gradient-to-br ${p.gradient} to-transparent border border-border/30 hover:scale-[1.005] transition-all`}>
                  <div className="flex items-start gap-4">
                    <div className="size-11 rounded-lg bg-gradient-sovereign flex items-center justify-center shadow-md shrink-0 group-hover:scale-110 group-hover:rotate-3 transition-all">
                      <p.icon className="size-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${p.accent}`}>{p.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                    </div>
                    <ArrowRight className={`size-4 ${p.accent} opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0 mt-1`} />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}

          <div className="mt-6 flex flex-wrap items-center gap-4 text-caption text-muted-foreground">
            <div className="flex items-center gap-2">
              <MapPin className="size-3.5 text-red" />
              <span>New Delhi headquarters</span>
            </div>
            <span className="opacity-30">·</span>
            <div className="flex items-center gap-2">
              <Phone className="size-3.5 text-emerald" />
              <span>Operations across 18 countries · 3 continents</span>
            </div>
            <span className="opacity-30">·</span>
            <div className="flex items-center gap-2">
              <Lock className="size-3.5 text-cyan" />
              <span>Discretion is absolute</span>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass-strong rounded-2xl p-6 md:p-7 gradient-border"
        >
          <div className="text-overline text-amber-gold mb-2">Direct Inquiry</div>
          <h3 className="text-heading-md mb-1">Send a message.</h3>
          <p className="text-caption text-muted-foreground mb-5">Every inquiry is reviewed personally.</p>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <input
                type="email"
                required
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-3 h-11 rounded-lg glass border border-border/60 text-body-sm focus:border-amber-gold focus:outline-none focus:ring-2 focus:ring-amber-gold/30 transition-all"
              />
            </div>
            <textarea
              required
              placeholder="Your message — what needs to be won?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 rounded-lg glass border border-border/60 text-body-sm focus:border-amber-gold focus:outline-none focus:ring-2 focus:ring-amber-gold/30 transition-all resize-none"
            />
            <Button
              type="submit"
              size="lg"
              className="w-full h-12 bg-gradient-sovereign shadow-lg"
              disabled={sent}
            >
              {sent ? <><Sparkles className="size-4" />Sent — Confirmation Inbound</> : <><Send className="size-4" />Send Inquiry</>}
            </Button>
          </form>
          <p className="mt-4 text-caption text-muted-foreground text-center">
            Discretion is absolute. Confidentiality is guaranteed. The standard is sovereign-grade.
          </p>
        </motion.div>
      </div>
    </Section>
  );
}
